import {
  AqElement,
  addToCart,
  afterAdd,
  config,
  fetchSectionDocument,
  formatMoney,
  qs,
  qsa,
  toast,
} from '@aq/core';

/* Product form: variant selection (Section Rendering API, with a client-side fallback),
   quantity stepper, add to cart and buy now. Used by the product page and the home featured product. */
class AqProductForm extends AqElement {
  #token = 0;

  init() {
    this.form = qs('form', this);
    this.variants = JSON.parse(qs('[data-variants]', this).textContent || '[]');
    this.idInput = qs('input[name="id"]', this.form);
    this.sectionId = this.dataset.sectionId;
    this.productUrl = this.dataset.productUrl;
    this.updateUrl = this.dataset.updateUrl === 'true';
    this.title = this.dataset.title;

    this.listen(this.form, 'change', (event) => this.#onChange(event));
    this.listen(this.form, 'click', (event) => this.#onClick(event));
    this.listen(this.form, 'submit', (event) => this.#onSubmit(event));
    this.listen(this.form, 'input', (event) => {
      if (event.target.matches('.aq-qty__input')) this.#clamp(event.target);
    });

    this.#markAvailability();
  }

  /* ---------- options ---------- */

  #selectedOptions() {
    return qsa('[data-option-index]', this.form).map((fieldset) => {
      const select = qs('select[data-option-select]', fieldset);
      return select ? select.value : qs('input[data-option-value]:checked', fieldset)?.value;
    });
  }

  #findVariant(options) {
    return this.variants.find((variant) => variant.options.every((value, index) => value === options[index]));
  }

  async #onChange(event) {
    if (!event.target.matches('[data-option-value], [data-option-select]')) return;

    const fieldset = event.target.closest('[data-option-index]');
    const label = qs('[data-selected-value]', fieldset);
    if (label) label.textContent = event.target.value;

    const variant = this.#findVariant(this.#selectedOptions());
    this.#markAvailability();

    if (!variant) {
      this.#setBuyState(false);
      return;
    }

    this.idInput.value = variant.id;
    if (this.updateUrl) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
    await this.#refresh(variant);
  }

  /* Marks values that cannot be bought given the other selected options. */
  #markAvailability() {
    const selected = this.#selectedOptions();
    qsa('[data-option-index]', this.form).forEach((fieldset) => {
      const index = Number(fieldset.dataset.optionIndex);
      qsa('input[data-option-value]', fieldset).forEach((input) => {
        const options = [...selected];
        options[index] = input.value;
        const match = this.#findVariant(options);
        const label = input.nextElementSibling;
        label?.classList.toggle('is-unavailable', !match || !match.available);
      });
    });
  }

  /* ---------- refresh ---------- */

  async #refresh(variant) {
    const token = ++this.#token;
    let doc = null;

    try {
      doc = await fetchSectionDocument(`${this.productUrl}?variant=${variant.id}`, this.sectionId);
    } catch {
      doc = null;
    }
    if (token !== this.#token) return;

    const synced = doc ? this.#syncFrom(doc) : false;
    if (!synced) this.#applyFallback(variant);

    document.dispatchEvent(
      new CustomEvent('aq:variant:change', { detail: { variant, mediaId: variant.media, doc, sectionId: this.sectionId } }),
    );
  }

  #syncFrom(doc) {
    let changed = false;
    qsa('[data-sync]', this).forEach((element) => {
      const next = qs(`[data-sync="${CSS.escape(element.dataset.sync)}"]`, doc);
      if (!next) return;
      const quantity = qs('.aq-qty__input', element)?.value;
      element.innerHTML = next.innerHTML;
      const input = qs('.aq-qty__input', element);
      if (quantity && input) input.value = Math.min(Number(quantity), Number(input.max || quantity));
      changed = true;
    });
    return changed;
  }

  /* Best-effort update from the variant data when the section cannot be fetched. */
  #applyFallback(variant) {
    qsa('.aq-price__now', this).forEach((element) => {
      element.textContent = formatMoney(variant.price);
    });
    qsa('.aq-price__was', this).forEach((element) => {
      element.textContent = variant.compare_at_price ? formatMoney(variant.compare_at_price) : '';
      element.hidden = !(variant.compare_at_price > variant.price);
    });
    this.#setBuyState(variant.available);
  }

  #setBuyState(available) {
    qsa('[data-add], [data-buy-now]', this).forEach((button) => {
      button.disabled = !available;
    });
    const label = qs('[data-add] span', this);
    if (label) label.textContent = available ? config.strings.addToCart : config.strings.unavailable;
  }

  /* ---------- quantity ---------- */

  #onClick(event) {
    const step = event.target.closest('[data-qty-step]');
    if (step) {
      const input = qs('.aq-qty__input', step.closest('[data-qty]'));
      input.value = Number(input.value || 1) + Number(step.dataset.qtyStep);
      this.#clamp(input);
      return;
    }
    if (event.target.closest('[data-buy-now]')) this.#submit({ checkout: true });
  }

  #clamp(input) {
    const min = Number(input.min || 1);
    const max = input.max ? Number(input.max) : Infinity;
    const value = Math.round(Number(input.value) || min);
    input.value = Math.min(Math.max(value, min), max);
  }

  /* ---------- add to cart ---------- */

  #onSubmit(event) {
    event.preventDefault();
    this.#submit();
  }

  async #submit({ checkout = false } = {}) {
    const addButton = qs('[data-add]', this.form);
    if (!addButton || addButton.disabled || this.hasAttribute('aria-busy')) return;

    const quantity = Number(qs('.aq-qty__input', this.form)?.value || 1);
    this.#showError('');
    this.setAttribute('aria-busy', 'true');
    addButton.setAttribute('aria-busy', 'true');

    try {
      const detail = await addToCart([{ id: Number(this.idInput.value), quantity }]);
      if (checkout) {
        window.location.href = config.routes.checkout;
        return;
      }
      afterAdd(detail, this.title);
    } catch (error) {
      this.#showError(error.message);
      toast(error.message);
    } finally {
      this.removeAttribute('aria-busy');
      addButton.removeAttribute('aria-busy');
    }
  }

  #showError(message) {
    const element = qs('[data-error]', this);
    if (!element) return;
    element.textContent = message;
    element.hidden = !message;
  }
}

if (!customElements.get('aq-product-form')) customElements.define('aq-product-form', AqProductForm);
